import imageCompression from 'browser-image-compression'
import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'
import { FileUploadRequest } from '@posx/core'
import secureStorage from '@xpos/utils/src/secure.storage.utils'
import { useConfigStore } from '@/stores/config'

export interface ImageUploadOptions {
  maxSizeMB?: number
  maxWidthOrHeight?: number
}

export interface UploadResult {
  file_url: string
  thumbnail_url: string
}

export type UploadTarget = 'merchant' | 'account'

// Compresses image blob with specified options
export async function compressImage(blob: Blob, { maxSizeMB = 0.5, maxWidthOrHeight = 1280 }: ImageUploadOptions = {}): Promise<Blob> {
  return imageCompression(blob as File, { maxSizeMB, maxWidthOrHeight, useWebWorker: false })
}

const blobToBase64 = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onloadend = () => typeof reader.result === 'string' ? resolve(reader.result.split(',')[1]) : reject(new Error('base64 conversion failed'))
  reader.onerror = () => reject(new Error('file read failed'))
  reader.readAsDataURL(blob)
})

// Uploads blob; account target uses Bearer-token endpoint (no merchant_uid required)
export async function uploadImage(blob: Blob, target: UploadTarget = 'merchant'): Promise<UploadResult> {
  if (target === 'account') {
    const baseUrl = import.meta.env.VITE_BASE_URL
    const name = uuidv4()
    const extension = blob.type.split('/')[1] || 'jpg'
    const base64 = await blobToBase64(blob)
    const { status, data } = await axios.post(`${baseUrl}api/v5/upload-image`,
      new FileUploadRequest({ base64, name, extension }),
      { headers: { Authorization: `Bearer ${secureStorage.getAccessToken()}` } })
    if (status !== 200 || data.error !== '') throw new Error(data.error || 'upload failed')
    const file_url = `${baseUrl}images/${name}.${extension}`
    return { file_url, thumbnail_url: file_url }
  }
  const service = await useConfigStore().getService()
  const res = await service.uploadFile(new File([blob], 'image.jpg', { type: blob.type || 'image/jpeg' }))
  return { file_url: res.file_url, thumbnail_url: res.thumbnail_url }
}
